<template>
  <div>
    <PageTitlebar>
      <template v-slot:title>
        <div class="flex items-center">
          <q-icon
            name="sym_r_conversion_path"
            class="q-mr-md"
            size="30px"
          />
          <div>
            <div class="flex items-center" style="margin-bottom: 1px">
              <h5>Conversions</h5>
            </div>
            <p
              class="text-font-secondary"
              style="font-size: 0.95em"
            >Track conversions and help better gauge your performance.</p>
          </div>
        </div>
      </template>
    </PageTitlebar>

    <DataTable
      table-key="advertiserConversionsOverview"
      row-key="id"
      :columns="columns"
      :rows="verifiedDomains"
      :pagination="{
        sortBy: 'time_verified',
        descending: true,
        page: 1,
        rowsPerPage: 5
      }"
      :loading="loading"
      :downloadable="false"
      :refreshable="false"
      :customizable="false"
      :filterable="false"
      :searchable="true"
      :disable-state="true"
    >
      <template v-slot:title>
        <div class="flex items-center">
          <div class="q-mr-md">
            <q-btn
              v-if="$store.state.user.record.type === 'admin'"
              round
              dense
              icon="sym_r_add"
              color="primary"
              disabled
            >
              <q-tooltip anchor="center right" self="center left">
                <b>You're on a Managed Program</b>
                <br>Message your account manager to add a new verified domain.
              </q-tooltip>
            </q-btn>
          </div>
          <div>
            <h6>Verified Domains</h6>
            <div class="text-font-secondary">Your domains that are verified for Conversions Pixel usage.</div>
          </div>
        </div>
      </template>
      <template v-slot:domain="row">
        <div class="text-weight-bold">
          {{ row.domain }}
        </div>
      </template>
      <template v-slot:status>
        <div class="flex items-center text-positive text-weight-medium">
          <q-icon name="sym_r_check_circle" color="positive" size="17px" class="q-mr-xs" style="margin-top: -1px" />
          <div>Verified</div>
        </div>
      </template>
    </DataTable>

    <q-card class="q-mt-lg">
      <q-card-section>
        <div class="flex items-start">
          <div class="q-mr-md">
            <q-avatar color="primary" text-color="white" size="32px" class="text-weight-bold">
              1
            </q-avatar>
          </div>
          <div class="col">
            <h6>Install Base Pixel Code</h6>
            <div class="text-font-secondary">
              Add the following snippet inside the <span class="text-weight-bold">&lt;head&gt;</span> tag on every page of your site.
            </div>
            <div class="relative-position">
              <q-btn
                label="Copy"
                color="grey-9"
                class="absolute"
                style="right: 8px; top: 8px; z-index: 1"
                @click="copyPixelCode('base')"
                no-caps
              />
              <pre class="bg-grey-10 text-white q-pa-md" style="border-radius: 4px; overflow-x: auto"><code><span class="text-amber-3">&lt;script</span> src<span class="text-light-green-4">="https://pixels.carscience.com/jfpe/v3.1/jfpe.min.js"</span><span class="text-amber-3">&gt;&lt;/script&gt;</span>
<span class="text-amber-3">&lt;script&gt;</span>
  <span class="text-italic">window</span>.jfpe.<span class="text-orange-6">init</span>();
<span class="text-amber-3">&lt;/script&gt;</span>
</code></pre>
            </div>
            <div class="text-font-secondary q-mt-xs">
              When installed, this will download a library of functions which can be used for conversions tracking. It also automatically tracks a single <span class="text-weight-bold">PageView</span> event each time it's initialized.
            </div>
          </div>
        </div>
      </q-card-section>
    </q-card>

    <q-card class="q-mt-lg">
      <q-card-section>
        <div class="flex items-start">
          <div class="q-mr-md">
            <q-avatar color="primary" text-color="white" size="32px" class="text-weight-bold">
              2
            </q-avatar>
          </div>
          <div class="col">
            <h6>Track Conversions Events</h6>
            <div class="text-font-secondary q-mt-xs">
              Once the base pixel code is installed, you can start tracking events by calling the <span class="text-weight-bold">window.jfpe.track()</span> method. See below for an example usage and a list of available conversions events.
            </div>
            <div class="relative-position" style="border-radius: 14px">
              <q-btn
                  label="Copy"
                  color="grey-9"
                  class="absolute"
                  style="right: 8px; top: 8px; z-index: 1"
                  @click="copyPixelCode('track')"
                  no-caps
              />
              <pre class="bg-grey-10 text-white q-pa-md" style="border-radius: 4px; overflow-x: auto"><code><span class="text-amber-3">&lt;script&gt;</span>
  <span class="text-italic">window</span>.jfpe.<span class="text-orange-6">track</span>(<span class="text-blue-3">"Search"</span>);

  <span class="text-blue-grey-4">// Or, with a channel value</span>
  <span class="text-italic">window</span>.jfpe.<span class="text-orange-6">track</span>(<span class="text-blue-3">"Search"</span>, {
    <span class="text-purple-3">channel</span>: <span class="text-blue-3">"example"</span>
  });
<span class="text-amber-3">&lt;/script&gt;</span>
</code></pre>
            </div>
            <div class="text-font-secondary q-mt-xs">
              A <span class="text-weight-bold">channel</span> value is optional, and can be used to track additional actions and help you further differentiate between conversions events. It can be any custom value.
              <br>For example, you could pass a channel value of "vehicle_not_available" on a "ContentView" event for a listing that's no longer active.
            </div>
            <div class="q-mt-md">
              <div class="text-weight-bold">Available Events:</div>
              <div class="text-font-secondary q-mb-sm">A list of all available conversions events. Track each event at the appropriate time to help improve your conversions reporting accuracy.</div>
              <q-markup-table flat style="border-radius: 4px !important;">
                <thead>
                <tr class="bg-gray-light q-tr--no-hover">
                  <th class="text-left">Event Name</th>
                  <th class="text-left">When To Track Event</th>
                </tr>
                </thead>
                <tbody>
                <tr class="q-tr--no-hover">
                  <td class="text-left text-weight-bold">PageView</td>
                  <td>Tracked automatically during initialization. Can be manually tracked again with any custom channel(s) as needed.</td>
                </tr>
                <tr class="q-tr--no-hover">
                  <td class="text-left text-weight-bold">Search</td>
                  <td>Track on each landing page or Search Results Page (SRP) load.</td>
                </tr>
                <tr class="q-tr--no-hover">
                  <td class="text-left text-weight-bold">ContentView</td>
                  <td>Track on each listing page or Vehicle Details Page (VDP) load.</td>
                </tr>
                <tr class="q-tr--no-hover">
                  <td class="text-left text-weight-bold">LeadSubmitted</td>
                  <td>Track on any of your core conversion objective(s). This includes objectives like lead submissions, or calls and chats.</td>
                </tr>
                </tbody>
              </q-markup-table>
            </div>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
/* eslint-disable no-useless-escape */
import PageTitlebar from "@/components/UI/PageTitlebar";
import DataTable from "@/components/UI/DataTable.vue";

export default {
  name: "AdvertiserConversionsOverview",
  components: {
    DataTable,
    PageTitlebar
  },
  data() {
    return {
      columns: [
        {
          name: "domain",
          label: "Domain",
          field: "domain",
          type: "dimension",
          sortable: true,
          hideField: true
        },
        {
          name: "status",
          label: "Status",
          field: "status",
          type: "dimension",
          sortable: false
        },
        {
          name: "time_last_tracked",
          label: "Time Last Tracked",
          field: "time_last_tracked",
          format: value => !value ? "-" : this.convertUtcToBusinessTimezone(value, "lll"),
          type: "metric",
          sortable: true
        },
        {
          name: "time_verified",
          label: "Time Verified",
          field: "time_verified",
          format: value => !value ? "-" : this.convertUtcToBusinessTimezone(value, "lll"),
          type: "metric",
          sortable: true
        },
      ]
    };
  },
  computed: {
    verifiedDomains() {
      return this.$store.state.business.record.verified_domains;
    }
  },
  mounted() {},
  methods: {
    copyPixelCode(type) {
      let code = "";

      if (type === "base") {
        code = "<script src=\"https://pixels.carscience.com/jfpe/v3.1/jfpe.min.js\"><\/script>\n" +
          "<script>" +
          "window.jfpe.init();" +
          "<\/script>";
      } else if (type === "track") {
        code = "<script>\n" +
          "window.jfpe.track(\"Search\");\n" +
          "// Or, with a channel value\n" +
          "window.jfpe.track(\"Search\", {\n" +
          "  channel: \"example\"\n" +
          "});\n" +
          "<\/script>";
      }

      this.copyToClipboard(code);
    }
  }
};
</script>

<style scoped lang="scss"></style>
